import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { translations } from "./translations";
import { BlogPage, BlogPost } from "./BlogComponents";
import { CookieBanner, CookiePolicyPage, PrivacyPolicyPage } from "./LegalComponents";
import { ServiceDetailPage } from "./ServiceComponents";
import { AboutPage } from "./AboutPage";

// Logo URLs
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_event-booking-pro-7/artifacts/6r7gbjry_logo%20800x800.png";
const LOGO_FULL = "https://customer-assets.emergentagent.com/job_event-booking-pro-7/artifacts/aj9y7e72_logo.png";

// Social Media Icons
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
);

// Service Icons - Minimalist SVG Icons
const ServiceIcons = {
  residence: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
  ),
  company: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
  ),
  realEstate: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
  ),
  investment: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
  ),
  digitalNomad: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
  ),
  consulting: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
  ),
  visa: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="14" x2="10" y2="14"/></svg>
  ),
  pilot: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
  ),
  education: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
  ),
  family: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  ),
  tax: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>
  ),
  citizenship: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
  )
};

// Package Icons - Minimalist SVG Icons
const PackageIcons = {
  discovery: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
  ),
  comprehensive: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
  ),
  goldenVisa: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
  )
};

// Icon mapping for services
const serviceIconMap = [
  ServiceIcons.residence,    // 01 Oturum İzni
  ServiceIcons.company,      // 02 Şirket Kurmak
  ServiceIcons.realEstate,   // 03 Gayrimenkul
  ServiceIcons.investment,   // 04 Golden Visa
  ServiceIcons.digitalNomad, // 05 Digital Nomad
  ServiceIcons.consulting,   // 06 Kapsamlı Danışmanlık
  ServiceIcons.visa,         // 07 Non-Lucrative
  ServiceIcons.pilot,        // 08 Pilotaj
  ServiceIcons.education,    // 09 Öğrenci
  ServiceIcons.family,       // 10 Aile Birleşimi
  ServiceIcons.tax,          // 11 Beckham
  ServiceIcons.citizenship   // 12 Vatandaşlık
];

const Home = ({ lang, setLang }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  
  const t = translations[lang];

  // Google Calendar links for each package (calendar first, then payment)
  const calendarLinks = {
    0: 'https://calendar.app.google/YXA8GWHxhxw5cB3M7', // Bilgilendirici Ön Görüşme - 60€, 30dk
    1: 'https://calendar.app.google/mVZgGnsGC8m7uu6D7'  // Kapsamlı Ön Görüşme - 80€, 45dk
  };

  // Handle package selection - redirect to Google Calendar
  const handlePackageSelect = (packageIndex) => {
    setSelectedPackage(packageIndex);
    window.open(calendarLinks[packageIndex], '_blank');
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle hash navigation (for links from other pages like blog)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const tabContent = {
    0: { // Vize Türleri
      title: lang === 'tr' ? 'Vize Seçenekleri' : lang === 'en' ? 'Visa Options' : 'Opciones de Visa',
      desc: lang === 'tr' ? 'Türk vatandaşları için birden fazla uzun süreli yerleşme yolu var.' : lang === 'en' ? 'Multiple long-term settlement options for Turkish citizens.' : 'Múltiples opciones de asentamiento a largo plazo para ciudadanos turcos.',
      items: lang === 'tr' ? [
        'Non-Lucrative: Pasif geliri olanlar için. €2.400+/ay şart.',
        'Digital Nomad: Uzaktan çalışanlar için. €2.646+/ay gelir.',
        'Golden Visa Alternatifleri: Nisan 2025\'ten itibaren program kapandı.',
        'Entrepreneur: İş kuracaklar için. İş planı şartı.',
        'Aile Birleşimi: Yakınınız İspanya\'daysa.',
        'Yüksek Vasıflı Çalışma: €50K+ geliri olan profesyoneller için.'
      ] : lang === 'en' ? [
        'Non-Lucrative: For those with passive income. €2,400+/month required.',
        'Digital Nomad: For remote workers. €2,646+/month income.',
        'Golden Visa Alternatives: Program closed as of April 2025.',
        'Entrepreneur: For business founders. Business plan required.',
        'Family Reunification: If your relative is in Spain.',
        'Highly Qualified Work: For professionals with €50K+ income.'
      ] : [
        'No Lucrativo: Para quienes tienen ingresos pasivos. €2.400+/mes requerido.',
        'Nómada Digital: Para trabajadores remotos. €2.646+/mes de ingresos.',
        'Alternativas Golden Visa: Programa cerrado desde abril 2025.',
        'Emprendedor: Para fundadores de negocios. Plan de negocio requerido.',
        'Reagrupación Familiar: Si tu familiar está en España.',
        'Trabajo Altamente Cualificado: Para profesionales con €50K+ de ingresos.'
      ],
      visual: {
        title: lang === 'tr' ? 'Onay Süreleri' : lang === 'en' ? 'Approval Times' : 'Tiempos de Aprobación',
        metrics: [
          { label: 'Non-Lucrative (45-90 gün)', value: 65, text: lang === 'tr' ? 'Orta' : lang === 'en' ? 'Medium' : 'Medio' },
          { label: 'Digital Nomad (30-60 gün)', value: 45, text: lang === 'tr' ? 'Hızlı' : lang === 'en' ? 'Fast' : 'Rápido' },
          { label: lang === 'tr' ? 'Yatırım Yoluyla (20-30 gün)' : lang === 'en' ? 'Investment (20-30 days)' : 'Inversión (20-30 días)', value: 25, text: lang === 'tr' ? 'En Hızlı' : lang === 'en' ? 'Fastest' : 'Más Rápido' },
          { label: 'Entrepreneur (90-180 gün)', value: 85, text: lang === 'tr' ? 'Uzun' : lang === 'en' ? 'Long' : 'Largo' }
        ]
      }
    },
    1: { // Yaşam Maliyeti
      title: lang === 'tr' ? 'Yaşam Maliyeti' : lang === 'en' ? 'Cost of Living' : 'Coste de Vida',
      desc: lang === 'tr' ? 'İspanya, Batı Avrupa\'nın en uygun fiyatlı ülkelerinden biri.' : lang === 'en' ? 'Spain is one of the most affordable countries in Western Europe.' : 'España es uno de los países más asequibles de Europa Occidental.',
      items: lang === 'tr' ? [
        'Kira (1+1): Barcelona €1.200-1.800 / Madrid €1.000-1.500 / Valencia €700-1.000',
        'Market: 2 kişi için aylık €300-400.',
        'Ulaşım: Aylık kart €40-55.',
        'Yemek: Restoranda menü €12-18.',
        'Sigorta: Aylık €60-150. Vize için zorunlu.',
        'İnternet: Fiber €30-40, GSM €20-30/ay.'
      ] : lang === 'en' ? [
        'Rent (1BR): Barcelona €1,200-1,800 / Madrid €1,000-1,500 / Valencia €700-1,000',
        'Groceries: €300-400/month for 2 people.',
        'Transport: Monthly pass €40-55.',
        'Dining: Restaurant menu €12-18.',
        'Insurance: €60-150/month. Required for visa.',
        'Internet: Fiber €30-40, Mobile €20-30/month.'
      ] : [
        'Alquiler (1Hab): Barcelona €1.200-1.800 / Madrid €1.000-1.500 / Valencia €700-1.000',
        'Compras: €300-400/mes para 2 personas.',
        'Transporte: Abono mensual €40-55.',
        'Comida: Menú restaurante €12-18.',
        'Seguro: €60-150/mes. Requerido para visa.',
        'Internet: Fibra €30-40, Móvil €20-30/mes.'
      ],
      visual: {
        title: lang === 'tr' ? 'Aylık Bütçe' : lang === 'en' ? 'Monthly Budget' : 'Presupuesto Mensual',
        metrics: [
          { label: 'Barcelona (€2.800/ay)', value: 90, text: lang === 'tr' ? 'Pahalı' : lang === 'en' ? 'Expensive' : 'Caro' },
          { label: 'Madrid (€2.500/ay)', value: 75, text: lang === 'tr' ? 'Orta' : lang === 'en' ? 'Medium' : 'Medio' },
          { label: 'Valencia (€1.800/ay)', value: 50, text: lang === 'tr' ? 'Uygun' : lang === 'en' ? 'Affordable' : 'Asequible' },
          { label: 'Alicante (€1.400/ay)', value: 35, text: lang === 'tr' ? 'Çok Uygun' : lang === 'en' ? 'Very Affordable' : 'Muy Asequible' }
        ]
      }
    },
    2: { // Vergi
      title: lang === 'tr' ? 'Vergi Sistemi' : lang === 'en' ? 'Tax System' : 'Sistema Fiscal',
      desc: lang === 'tr' ? 'Beckham Yasası ile ilk 6 yıl %24 sabit oran avantajı.' : lang === 'en' ? 'Beckham Law offers flat 24% rate for first 6 years.' : 'La Ley Beckham ofrece tasa fija del 24% durante los primeros 6 años.',
      items: lang === 'tr' ? [
        'Beckham Yasası: İlk 6 yıl %24 sabit.',
        'IRPF: €0-12.450: %19 → üzeri %30-47.',
        'NIE: Her resmi işlem için şart.',
        'Çifte Vergilendirme: TR-ES anlaşması yok. Danışın.',
        'Autónomo: İlk yıl €80/ay.',
        'IVA: %21 genel, gıdada %4-10.'
      ] : lang === 'en' ? [
        'Beckham Law: Flat 24% for first 6 years.',
        'IRPF: €0-12,450: 19% → above 30-47%.',
        'NIE: Required for all official transactions.',
        'Double Taxation: No TR-ES agreement. Consult us.',
        'Autónomo: €80/month first year.',
        'VAT: 21% general, 4-10% on food.'
      ] : [
        'Ley Beckham: 24% fijo durante los primeros 6 años.',
        'IRPF: €0-12.450: 19% → superior 30-47%.',
        'NIE: Requerido para todas las transacciones oficiales.',
        'Doble Imposición: Sin acuerdo TR-ES. Consúltenos.',
        'Autónomo: €80/mes primer año.',
        'IVA: 21% general, 4-10% en alimentación.'
      ],
      visual: {
        title: lang === 'tr' ? 'Beckham Avantajı' : lang === 'en' ? 'Beckham Advantage' : 'Ventaja Beckham',
        metrics: [
          { label: lang === 'tr' ? 'Normal Oran' : lang === 'en' ? 'Normal Rate' : 'Tasa Normal', value: 45, text: '%35-45' },
          { label: 'Beckham', value: 24, text: '%24' }
        ]
      }
    },
    3: { // Şehirler
      title: lang === 'tr' ? 'Hangi Şehir Size Göre?' : lang === 'en' ? 'Which City is Right for You?' : '¿Qué Ciudad es Adecuada para Ti?',
      desc: lang === 'tr' ? 'Her şehir farklı bir yaşam sunar.' : lang === 'en' ? 'Each city offers a different lifestyle.' : 'Cada ciudad ofrece un estilo de vida diferente.',
      items: lang === 'tr' ? [
        'Barcelona: Kozmopolit, Türk topluluğu, deniz+şehir.',
        'Madrid: İş merkezi, gece hayatı, kültür.',
        'Valencia: Akdeniz iklimi, uygun kira.',
        'Alicante: Emekliler için ideal, sakin.',
        'Malaga: Digital nomadlar için popüler.',
        'Bilbao: Kuzey İspanya, kültür şehri.'
      ] : lang === 'en' ? [
        'Barcelona: Cosmopolitan, Turkish community, sea+city.',
        'Madrid: Business center, nightlife, culture.',
        'Valencia: Mediterranean climate, affordable rent.',
        'Alicante: Ideal for retirees, peaceful.',
        'Malaga: Popular for digital nomads.',
        'Bilbao: Northern Spain, cultural city.'
      ] : [
        'Barcelona: Cosmopolita, comunidad turca, mar+ciudad.',
        'Madrid: Centro de negocios, vida nocturna, cultura.',
        'Valencia: Clima mediterráneo, alquiler asequible.',
        'Alicante: Ideal para jubilados, tranquilo.',
        'Málaga: Popular para nómadas digitales.',
        'Bilbao: Norte de España, ciudad cultural.'
      ],
      visual: {
        title: lang === 'tr' ? 'Türkler Arasında Popülerlik' : lang === 'en' ? 'Popularity Among Turks' : 'Popularidad Entre Turcos',
        metrics: [
          { label: 'Barcelona', value: 92, text: '%92' },
          { label: 'Madrid', value: 85, text: '%85' },
          { label: 'Malaga', value: 68, text: '%68' },
          { label: 'Valencia', value: 55, text: '%55' }
        ]
      }
    }
  };

  return (
    <div className="app" data-testid="nova-iberian-app">
      {/* SEO - Full Meta Tags for Home Page */}
      <Helmet>
        <title>{lang === 'tr' ? 'İspanya\'da Oturum İzni ve Yatırım Danışmanlığı | Nova Iberian' : lang === 'en' ? 'Spain Residence Permit & Investment Consulting | Nova Iberian' : 'Permiso de Residencia e Inversión en España | Nova Iberian'}</title>
        <meta name="description" content={lang === 'tr' ? 'İspanya vize danışmanlığı ve oturum izni konusunda uzman Türk danışmanlık şirketi. Barcelona merkezli Nova Iberian ile dijital göçmen vizesi, girişimci vizesi, Beckham Kanunu vergi avantajı ve İspanya\'da şirket kurma süreçlerinizi Türkçe destek ile yönetin.' : lang === 'en' ? 'Expert Turkish consulting company for Spain visa and residence permits. Manage your digital nomad visa, entrepreneur visa, Beckham Law tax benefits and company formation in Spain with Barcelona-based Nova Iberian.' : 'Empresa consultora turca experta en visados y permisos de residencia en España. Gestione su visa de nómada digital, visa de emprendedor, beneficios fiscales de la Ley Beckham y constitución de empresas en España con Nova Iberian en Barcelona.'} />
        <link rel="canonical" href="https://novaiberian.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://novaiberian.com/" />
        <meta property="og:title" content={lang === 'tr' ? 'İspanya\'da Oturum İzni ve Yatırım Danışmanlığı | Nova Iberian' : lang === 'en' ? 'Spain Residence Permit & Investment Consulting | Nova Iberian' : 'Permiso de Residencia e Inversión en España | Nova Iberian'} />
        <meta property="og:description" content={lang === 'tr' ? 'İspanya\'ya yerleşmek isteyenler için profesyonel danışmanlık. Oturum izni, Digital Nomad vizesi, şirket kurulumu ve gayrimenkul yatırımı.' : lang === 'en' ? 'Professional consulting for those who want to settle in Spain. Residence permit, Digital Nomad visa, company formation and real estate investment.' : 'Consultoría profesional para quienes quieren establecerse en España. Permiso de residencia, visa Nómada Digital, constitución de empresas e inversión inmobiliaria.'} />
        <meta property="og:image" content="https://novaiberian.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@IberianNov846" />
        <meta name="twitter:title" content={lang === 'tr' ? 'İspanya\'da Oturum İzni ve Yatırım Danışmanlığı | Nova Iberian' : lang === 'en' ? 'Spain Residence Permit & Investment Consulting | Nova Iberian' : 'Permiso de Residencia e Inversión en España | Nova Iberian'} />
        <meta name="twitter:description" content={lang === 'tr' ? 'İspanya\'ya yerleşmek isteyenler için profesyonel danışmanlık. Oturum izni, Digital Nomad vizesi, şirket kurulumu.' : lang === 'en' ? 'Professional consulting for settling in Spain. Residence permit, Digital Nomad visa, company formation.' : 'Consultoría profesional para establecerse en España. Permiso de residencia, visa Nómada Digital, constitución de empresas.'} />
        <meta name="twitter:image" content="https://novaiberian.com/og-image.png" />
      </Helmet>
      
      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} data-testid="navbar">
        <div className="nav-inner">
          <a href="#" className="nav-logo" data-testid="nav-logo">
            <img src={LOGO_ICON} alt="Nova Iberian" className="nav-logo-icon" />
            <span className="nav-logo-text">Nova <span>Iberian</span></span>
          </a>
          
          <div className="nav-links">
            <a href="#hizmetler" data-testid="nav-services">{t.nav.services}</a>
            <Link to="/can-cagatay-celikkol" data-testid="nav-about">{t.nav.about}</Link>
            <a href="#referanslar" data-testid="nav-testimonials">{t.nav.testimonials}</a>
            <a href="#ispanya-bilgisi" data-testid="nav-guide">{t.nav.guide}</a>
            <a href="/blog" data-testid="nav-blog">{t.nav.blog}</a>
            <a href="#iletisim" data-testid="nav-contact">{t.nav.contact}</a>
            
            {/* Social Media Icons in Nav */}
            <div className="nav-social-icons">
              <a href="https://x.com/IberianNov846" target="_blank" rel="noopener noreferrer"><TwitterIcon /></a>
              <a href="https://www.instagram.com/nova_iberian" target="_blank" rel="noopener noreferrer"><InstagramIcon /></a>
              <a href="https://youtube.com/@cancagataycelikkol?si=dDAtv-bmmLjSZ-FW" target="_blank" rel="noopener noreferrer"><YouTubeIcon /></a>
              <a href="https://www.linkedin.com/company/nova-iberian-consulting-group-limited-company/" target="_blank" rel="noopener noreferrer"><LinkedInIcon /></a>
              <a href="https://www.tiktok.com/@nova_iberian" target="_blank" rel="noopener noreferrer"><TikTokIcon /></a>
            </div>
          </div>
          
          <div className="nav-right">
            <div className="lang-dropdown" data-testid="lang-switcher">
              <button 
                className="lang-dropdown-btn"
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                data-testid="lang-dropdown-btn"
              >
                <span className="lang-code">{lang === 'tr' ? 'TR' : lang === 'en' ? 'EN' : 'ES'}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              {langDropdownOpen && (
                <div className="lang-dropdown-menu">
                  <button 
                    className={`lang-dropdown-item ${lang === 'tr' ? 'active' : ''}`}
                    onClick={() => { setLang('tr'); setLangDropdownOpen(false); }}
                  >
                    Türkçe
                  </button>
                  <button 
                    className={`lang-dropdown-item ${lang === 'en' ? 'active' : ''}`}
                    onClick={() => { setLang('en'); setLangDropdownOpen(false); }}
                  >
                    English
                  </button>
                  <button 
                    className={`lang-dropdown-item ${lang === 'es' ? 'active' : ''}`}
                    onClick={() => { setLang('es'); setLangDropdownOpen(false); }}
                  >
                    Español
                  </button>
                </div>
              )}
            </div>
            <a href="#randevu" className="btn-nav" data-testid="nav-cta">{t.nav.appointment}</a>
            
            <button 
              className="mobile-menu-btn" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
        
        <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
          {/* Social Media Icons at top of mobile menu */}
          <div className="mobile-social-icons">
            <a href="https://x.com/IberianNov846" target="_blank" rel="noopener noreferrer"><TwitterIcon /></a>
            <a href="https://www.instagram.com/nova_iberian" target="_blank" rel="noopener noreferrer"><InstagramIcon /></a>
            <a href="https://youtube.com/@cancagataycelikkol?si=dDAtv-bmmLjSZ-FW" target="_blank" rel="noopener noreferrer"><YouTubeIcon /></a>
            <a href="https://www.linkedin.com/company/nova-iberian-consulting-group-limited-company/" target="_blank" rel="noopener noreferrer"><LinkedInIcon /></a>
            <a href="https://www.tiktok.com/@nova_iberian" target="_blank" rel="noopener noreferrer"><TikTokIcon /></a>
          </div>
          <a href="#hizmetler" onClick={() => setMobileMenuOpen(false)}>{t.nav.services}</a>
          <Link to="/can-cagatay-celikkol" onClick={() => setMobileMenuOpen(false)}>{t.nav.about}</Link>
          <a href="#referanslar" onClick={() => setMobileMenuOpen(false)}>{t.nav.testimonials}</a>
          <a href="#ispanya-bilgisi" onClick={() => setMobileMenuOpen(false)}>{t.nav.guide}</a>
          <a href="/blog" onClick={() => setMobileMenuOpen(false)}>{t.nav.blog}</a>
          <a href="#iletisim" onClick={() => setMobileMenuOpen(false)}>{t.nav.contact}</a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-bg-image" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_event-booking-pro-7/artifacts/62nm8vuc_barcelona-city-aerial-view-2026-03-17-14-37-01-utc.jpg')` }}></div>
        <div className="hero-inner">
          <div className="hero-content">
            <h1>{t.hero.titleStart} <em>{t.hero.titleEm1}</em> {t.hero.titleMid} <em>{t.hero.titleEm2}</em> {t.hero.titleEnd}</h1>
            <p className="hero-sub">{t.hero.sub}</p>
            <div className="hero-actions">
              <a href="#randevu" className="btn-primary" data-testid="hero-cta-primary">{t.hero.cta1}</a>
              <a href="#hizmetler" className="btn-ghost" data-testid="hero-cta-secondary">{t.hero.cta2}</a>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-num">50+</span>
                <span className="stat-label">{t.hero.stat1}</span>
              </div>
              <div className="stat-item">
                <span className="stat-num">%92</span>
                <span className="stat-label">{t.hero.stat2}</span>
              </div>
              <div className="stat-item">
                <span className="stat-num">4</span>
                <span className="stat-label">{t.hero.stat3}</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-card" data-testid="hero-card">
              <div className="floating-badge">{t.hero.approved}</div>
              <div className="hero-card-header">
                <span className="card-tag">{t.hero.cardTag}</span>
                <span className="card-date">Mar 2026</span>
              </div>
              <div className="process-steps">
                <div className="process-step">
                  <div className="step-icon done">✓</div>
                  <span className="step-text">{t.hero.step1}</span>
                </div>
                <div className="process-step">
                  <div className="step-icon done">✓</div>
                  <span className="step-text">{t.hero.step2}</span>
                </div>
                <div className="process-step">
                  <div className="step-icon done">✓</div>
                  <span className="step-text">{t.hero.step3}</span>
                </div>
                <div className="process-step">
                  <div className="step-icon active">→</div>
                  <span className="step-text">{t.hero.step4}</span>
                </div>
                <div className="process-step">
                  <div className="step-icon pending">⋯</div>
                  <span className="step-text">{t.hero.step5}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="hizmetler" className="services-section" data-testid="services-section">
        <div className="section-inner">
          <div className="services-header">
            <div className="sec-eyebrow" style={{ color: 'var(--gold)' }}>{t.services.eyebrow}</div>
            <h2 className="on-dark">{t.services.title} <em>{t.services.titleEm}</em> {t.services.titleEnd}</h2>
            <div className="divider"></div>
            <p className="sec-sub on-dark">{t.services.sub}</p>
          </div>
          <div className="services-grid services-grid-6">
            {t.services.items.map((service, index) => {
              const IconComponent = serviceIconMap[index];
              // Map TR slugs to language-specific slugs
              const slugMappings = {
                'gayrimenkul-yatirim-danismanligi': { tr: 'gayrimenkul-yatirim-danismanligi', en: 'real-estate-investment-consulting', es: 'consultoria-inversion-inmobiliaria' },
                'sirket-kurulumu': { tr: 'sirket-kurulumu', en: 'company-formation', es: 'constitucion-empresa' },
                'oturum-izni': { tr: 'oturum-izni', en: 'residence-permit', es: 'permiso-residencia' },
                'kapsamli-danismanlik': { tr: 'kapsamli-danismanlik', en: 'comprehensive-consulting', es: 'consultoria-integral' },
                'vergi-danismanligi': { tr: 'vergi-danismanligi', en: 'tax-consulting', es: 'consultoria-fiscal' },
                'pilotaj-egitimi': { tr: 'pilotaj-egitimi', en: 'pilot-training', es: 'formacion-piloto' }
              };
              const localizedSlug = slugMappings[service.slug]?.[lang] || service.slug;
              return (
                <Link 
                  key={index} 
                  to={`/${localizedSlug}`}
                  className="service-card service-card-compact" 
                  data-testid={`service-card-${index}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <span className="service-num">{service.num}</span>
                  <span className="service-icon">{IconComponent && <IconComponent />}</span>
                  <div className="service-title">{service.title}</div>
                  <div className="service-desc">{service.desc}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Biz Kimiz Section */}
      <section id="biz-kimiz" className="about-section" data-testid="about-section">
        <div className="section-inner">
          <div className="sec-eyebrow">{t.about.eyebrow}</div>
          <h2>
            {lang === 'tr' && <>Nova Iberian'ın <em>Arkasındaki İsim</em></>}
            {lang === 'en' && <>The Name Behind <em>Nova Iberian</em></>}
            {lang === 'es' && <>El Nombre Detrás de <em>Nova Iberian</em></>}
          </h2>
          <div className="divider"></div>
          
          <div className="about-content">
            <div className="about-image">
              <img src="https://customer-assets.emergentagent.com/job_event-booking-pro-7/artifacts/vpf41mnd_%C3%A7a%C4%9Fatay.jfif" alt="Can Çağatay Çelikkol - Nova Iberian Kurucusu" />
            </div>
            <div className="about-text">
              <h3>Can Çağatay Çelikkol</h3>
              <p className="about-role">{t.about.role}</p>
              <p className="about-bio">{t.about.bio}</p>
              <div className="about-social">
                <a href="https://www.linkedin.com/in/can-%C3%A7a%C4%9Fatay-%C3%A7elikkol-106a0a159/" target="_blank" rel="noopener noreferrer" className="social-link-icon"><LinkedInIcon /></a>
                <a href="https://www.instagram.com/nova_iberian" target="_blank" rel="noopener noreferrer" className="social-link-icon"><InstagramIcon /></a>
                <a href="https://x.com/IberianNov846" target="_blank" rel="noopener noreferrer" className="social-link-icon"><TwitterIcon /></a>
                <a href="https://youtube.com/@cancagataycelikkol?si=dDAtv-bmmLjSZ-FW" target="_blank" rel="noopener noreferrer" className="social-link-icon"><YouTubeIcon /></a>
                <a href="https://www.tiktok.com/@nova_iberian" target="_blank" rel="noopener noreferrer" className="social-link-icon"><TikTokIcon /></a>
                <a href="mailto:cagatay@novaiberian.com" className="social-link-icon"><EmailIcon /></a>
              </div>
              <Link to="/can-cagatay-celikkol" className="btn-ghost about-more-link" style={{ marginTop: '20px' }}>
                {lang === 'tr' ? 'Daha Fazla Bilgi →' : lang === 'en' ? 'Learn More →' : 'Más Información →'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Appointment Section */}
      <section id="randevu" className="appointment-section" data-testid="appointment-section">
        <div className="section-inner">
          <div className="sec-eyebrow" style={{ color: 'var(--gold)' }}>{t.appointment.eyebrow}</div>
          <h2 className="on-dark">{t.appointment.title} <em>{t.appointment.titleEm}</em>.</h2>
          <div className="divider"></div>
          <p className="sec-sub on-dark">{t.appointment.sub}</p>
          
          <div className="appointment-packages-grid two-packages">
            {/* Package 0 - Bilgilendirici Ön Görüşme */}
            <div 
              className={`package-card-new ${selectedPackage === 0 ? 'selected' : ''}`}
              onClick={() => handlePackageSelect(0)}
              data-testid="package-0"
            >
              <div className="pkg-badge popular">{lang === 'tr' ? 'En Popüler' : lang === 'en' ? 'Most Popular' : 'Más Popular'}</div>
              <div className="pkg-icon"><PackageIcons.comprehensive /></div>
              <div className="pkg-name-new">{lang === 'tr' ? 'Bilgilendirici Ön Görüşme' : lang === 'en' ? 'Informative Pre-Meeting' : 'Reunión Informativa Previa'}</div>
              <div className="pkg-desc-new">{t.appointment.pkg1.desc}</div>
              <div className="pkg-price-new">60€</div>
              <div className="pkg-duration-new">30 {lang === 'tr' ? 'dk' : 'min'} · Online</div>
              <div className="pkg-features">
                <span>✓ {lang === 'tr' ? 'Vize stratejisi' : lang === 'en' ? 'Visa strategy' : 'Estrategia de visa'}</span>
                <span>✓ {lang === 'tr' ? 'Belge listesi' : lang === 'en' ? 'Document checklist' : 'Lista de documentos'}</span>
                <span>✓ {lang === 'tr' ? 'Yol haritası' : lang === 'en' ? 'Roadmap' : 'Hoja de ruta'}</span>
              </div>
            </div>

            {/* Package 1 - Kapsamlı Ön Görüşme */}
            <div 
              className={`package-card-new ${selectedPackage === 1 ? 'selected' : ''}`}
              onClick={() => handlePackageSelect(1)}
              data-testid="package-1"
            >
              <div className="pkg-badge premium">{lang === 'tr' ? 'Premium' : 'Premium'}</div>
              <div className="pkg-icon"><PackageIcons.goldenVisa /></div>
              <div className="pkg-name-new">{lang === 'tr' ? 'Kapsamlı Ön Görüşme' : lang === 'en' ? 'Comprehensive Pre-Meeting' : 'Reunión Previa Integral'}</div>
              <div className="pkg-desc-new">{t.appointment.pkg2.desc}</div>
              <div className="pkg-price-new">80€</div>
              <div className="pkg-duration-new">45 {lang === 'tr' ? 'dk' : 'min'} · Online / {lang === 'tr' ? 'Yüz yüze' : lang === 'en' ? 'In-person' : 'Presencial'}</div>
              <div className="pkg-features">
                <span>✓ {lang === 'tr' ? 'Yatırım analizi' : lang === 'en' ? 'Investment analysis' : 'Análisis de inversión'}</span>
                <span>✓ {lang === 'tr' ? 'Hukuki danışmanlık' : lang === 'en' ? 'Legal consulting' : 'Asesoría legal'}</span>
                <span>✓ {lang === 'tr' ? 'Vergi planlaması' : lang === 'en' ? 'Tax planning' : 'Planificación fiscal'}</span>
              </div>
            </div>
          </div>

          <p className="appointment-note-standalone">
            {lang === 'tr' ? 'Randevu sonrası Stripe ile ödeme sayfasına yönlendirileceksiniz.' : lang === 'en' ? 'After booking, you will be redirected to the Stripe payment page.' : 'Después de la reserva, será redirigido a la página de pago de Stripe.'}
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="referanslar" className="testimonials-section" data-testid="testimonials-section">
        <div className="section-inner">
          <div className="sec-eyebrow">{t.testimonials.eyebrow}</div>
          <h2>{t.testimonials.title} <em>{t.testimonials.titleEm}</em>{t.testimonials.titleEnd}</h2>
          <div className="divider"></div>
          <div className="testimonials-grid">
            {t.testimonials.items.map((item, idx) => (
              <div key={idx} className="testimonial-card" data-testid={`testimonial-${idx}`}>
                <div className="stars">★★★★★</div>
                <div className="testimonial-text">"{item.text}"</div>
                <div className="testimonial-author">
                  <div className="author-avatar">{item.name.split(' ').map(n => n[0]).join('')}</div>
                  <div>
                    <div className="author-name">{item.name}</div>
                    <div className="author-info">{item.info}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section with Tabs - İspanya Rehberi */}
      <section id="ispanya-bilgisi" className="info-section" data-testid="info-section">
        <div className="section-inner">
          <div className="sec-eyebrow">{t.info.eyebrow}</div>
          <h2>{t.info.title} <em>{t.info.titleEm}</em> {t.info.titleEnd}</h2>
          <div className="divider"></div>
          <p className="sec-sub">{t.info.sub}</p>
          
          <div className="tabs-container">
            <div className="tab-headers" data-testid="tab-headers">
              {t.info.tabs.map((tab, index) => (
                <button
                  key={index}
                  className={`tab-btn ${activeTab === index ? 'active' : ''}`}
                  onClick={() => setActiveTab(index)}
                  data-testid={`tab-btn-${index}`}
                >
                  {tab}
                </button>
              ))}
            </div>
            
            <div className={`tab-pane active`} data-testid="active-tab-pane">
              <div className="info-content">
                <h3>{tabContent[activeTab].title}</h3>
                <p>{tabContent[activeTab].desc}</p>
                <ul className="info-list">
                  {tabContent[activeTab].items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="info-visual">
                <div className="info-visual-title">{tabContent[activeTab].visual.title}</div>
                {tabContent[activeTab].visual.metrics.map((metric, idx) => (
                  <div key={idx} className="info-metric">
                    <div className="metric-label">{metric.label}</div>
                    <div className="metric-bar-wrap">
                      <div className="metric-bar" style={{ width: `${metric.value}%` }}></div>
                    </div>
                    <div className="metric-value">{metric.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section - Link to Blog Page */}
      <section className="blog-preview-section" data-testid="blog-preview">
        <div className="section-inner">
          <div className="sec-eyebrow">{lang === 'tr' ? 'Blog' : 'Blog'}</div>
          <h2>{lang === 'tr' ? 'Güncel ' : lang === 'en' ? 'Latest ' : 'Últimos '}<em>{lang === 'tr' ? 'Yazılarımız' : lang === 'en' ? 'Articles' : 'Artículos'}</em></h2>
          <div className="divider"></div>
          <p className="sec-sub">{lang === 'tr' ? 'İspanya\'ya taşınma sürecinizde size rehberlik edecek içerikler' : lang === 'en' ? 'Content to guide you through your relocation to Spain' : 'Contenido para guiarte en tu mudanza a España'}</p>
          <a href="/blog" className="btn-primary" style={{ marginTop: '32px', display: 'inline-block' }}>
            {lang === 'tr' ? 'Tüm Yazıları Gör →' : lang === 'en' ? 'See All Articles →' : 'Ver Todos los Artículos →'}
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" data-testid="faq-section">
        <div className="section-inner">
          <div className="sec-eyebrow">{t.faq.eyebrow}</div>
          <h2>{t.faq.title} <em>{t.faq.titleEm}</em>.</h2>
          <div className="divider"></div>
          <div className="faq-grid">
            {t.faq.items.map((item, idx) => (
              <div 
                key={idx} 
                className={`faq-item ${openFaq === idx ? 'open' : ''}`}
                data-testid={`faq-item-${idx}`}
              >
                <div className="faq-q" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}>
                  <span>{item.q}</span>
                  <span className="faq-icon">+</span>
                </div>
                <div className="faq-a">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="iletisim" className="contact-section" data-testid="contact-section">
        <div className="section-inner">
          <div className="sec-eyebrow">{t.contact.eyebrow}</div>
          <h2>{t.contact.title} <em>{t.contact.titleEm}</em> {t.contact.titleEnd}</h2>
          <div className="divider"></div>
          <div className="contact-grid">
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">✉️</div>
                <div>
                  <div className="contact-item-label">{t.contact.email}</div>
                  <div className="contact-item-value"><a href="mailto:info@novaiberian.com">info@novaiberian.com</a></div>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div>
                  <div className="contact-item-label">{t.contact.phone}</div>
                  <div className="contact-item-value"><a href="tel:+34634450804">+34 634 45 08 04</a></div>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">💬</div>
                <div>
                  <div className="contact-item-label">{t.contact.whatsapp}</div>
                  <div className="contact-item-value"><a href="https://wa.me/34634450804" target="_blank" rel="noopener noreferrer">+34 634 45 08 04</a></div>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div>
                  <div className="contact-item-label">{lang === 'tr' ? 'Ofis' : lang === 'en' ? 'Office' : 'Oficina'}</div>
                  <div className="contact-item-value">Barcelona, {lang === 'tr' ? 'İspanya' : lang === 'en' ? 'Spain' : 'España'}</div>
                </div>
              </div>
              
              {/* Social Media Links */}
              <div className="social-links" data-testid="social-links">
                <a href="https://x.com/IberianNov846" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-twitter">
                  <TwitterIcon />
                </a>
                <a href="https://www.instagram.com/nova_iberian" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-instagram">
                  <InstagramIcon />
                </a>
                <a href="https://youtube.com/@cancagataycelikkol?si=dDAtv-bmmLjSZ-FW" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-youtube">
                  <YouTubeIcon />
                </a>
                <a href="https://www.linkedin.com/company/nova-iberian-consulting-group-limited-company/" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-linkedin">
                  <LinkedInIcon />
                </a>
                <a href="https://www.tiktok.com/@nova_iberian" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-tiktok">
                  <TikTokIcon />
                </a>
              </div>
              
              <div style={{ marginTop: '24px' }}>
                <a href="#randevu" className="btn-primary" data-testid="contact-cta">{t.contact.cta}</a>
              </div>
            </div>
            
            <div>
              <div className="contact-city-single">
                <div className="contact-city-name">{t.contact.barcelona.name}</div>
                <div className="contact-city-desc">{t.contact.barcelona.desc}<br />{t.contact.barcelona.sub}</div>
                <div className="contact-city-address">Carrer de la Marina, 68, Bj 2<br />Sant Martí, Barcelona 08005</div>
              </div>
              
              {/* Google Maps Embed */}
              <div className="contact-map" data-testid="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2993.5!2d2.1947!3d41.3975!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4a31f5f8b8b8b%3A0x1234567890abcdef!2sCarrer%20de%20la%20Marina%2C%2068%2C%2008005%20Barcelona!5e0!3m2!1sen!2ses!4v1234567890"
                  width="100%"
                  height="250"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Nova Iberian Barcelona Office"
                ></iframe>
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=Carrer+de+la+Marina+68+Barcelona+Spain+08005" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="map-external-link"
                  data-testid="map-link"
                >
                  {lang === 'tr' ? 'Google Haritalar\'da Aç →' : lang === 'en' ? 'Open in Google Maps →' : 'Abrir en Google Maps →'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-inner">
          <a href="#" className="footer-logo">
            <img src={LOGO_ICON} alt="Nova Iberian" className="footer-logo-icon" />
            <span className="footer-logo-text">Nova <span>Iberian</span></span>
          </a>
          <div className="footer-text">© 2026 Nova Iberian. {t.footer.rights}</div>
          <div className="footer-links">
            <Link to="/aydinlatma-metni">{lang === 'tr' ? 'Aydınlatma Metni' : lang === 'en' ? 'Privacy Notice' : 'Aviso de Privacidad'}</Link>
            <Link to="/cerez-politikasi">{lang === 'tr' ? 'Çerez Politikası' : lang === 'en' ? 'Cookie Policy' : 'Política de Cookies'}</Link>
          </div>
        </div>
      </footer>
      
      {/* Cookie Consent Banner */}
      <CookieBanner lang={lang} />
    </div>
  );
};

function App() {
  // Persist language preference in localStorage
  const [lang, setLang] = useState(() => {
    const savedLang = localStorage.getItem('novaiberian_lang');
    return savedLang || 'tr';
  });

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('novaiberian_lang', lang);
  }, [lang]);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home lang={lang} setLang={setLang} />} />
          <Route path="/blog" element={<BlogPage lang={lang} setLang={setLang} />} />
          <Route path="/blog/:slug" element={<BlogPost lang={lang} setLang={setLang} />} />
          {/* Service routes - without /hizmet/ prefix */}
          <Route path="/sirket-kurulumu" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/company-formation" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/constitucion-empresa" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/gayrimenkul-yatirim-danismanligi" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/real-estate-investment-consulting" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/consultoria-inversion-inmobiliaria" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/oturum-izni" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/residence-permit" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/permiso-residencia" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/kapsamli-danismanlik" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/comprehensive-consulting" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/consultoria-integral" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/vergi-danismanligi" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/tax-consulting" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/consultoria-fiscal" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/pilotaj-egitimi" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/pilot-training" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          <Route path="/formacion-piloto" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          {/* Legacy routes - redirect to new URLs */}
          <Route path="/hizmet/:slug" element={<ServiceDetailPage lang={lang} setLang={setLang} />} />
          {/* About Page */}
          <Route path="/can-cagatay-celikkol" element={<AboutPage lang={lang} setLang={setLang} />} />
          {/* Legal Pages */}
          <Route path="/cerez-politikasi" element={<CookiePolicyPage lang={lang} setLang={setLang} />} />
          <Route path="/aydinlatma-metni" element={<PrivacyPolicyPage lang={lang} setLang={setLang} />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
